/**
 * PERF-H9: idle-time warm-up for the chunk(s) an authenticated visitor is about to need.
 *
 * WHY THIS EXISTS. PERF-H8 put LinkTable (and, separately, LinkForm/ErrorHistory/ExternalApiKeys)
 * behind `React.lazy()` so a visitor who only logs in never downloads MUI/material-react-table or
 * react-select. But `/currentLinks` (LinkTable) is exactly where every authenticated visitor lands
 * right after login (see App.tsx's routes), and Layout's corporate org-select (OrgSelect, PERF-H9)
 * renders alongside it immediately for corporate users. Left alone, the lazy split is only a
 * DEFERRAL for that visitor, not a reduction -- the same bytes still block the first thing they
 * actually look at, just one navigation later than before the split existed. The review's verifier
 * called this out explicitly and asked for the split to be paired with a prefetch/idle-time
 * `import()` warm-up so the chunk is typically already cached by the time login completes.
 *
 * WHY idle time, not immediately. Firing these `import()`s synchronously on login would compete
 * with whatever's still rendering (Login/Home's own network calls, the OAuth exchange settling,
 * etc.) for the same connection and CPU. `requestIdleCallback` schedules the fetch for whenever the
 * browser is next idle, so it doesn't compete with anything users are actually waiting on; Safari
 * has no `requestIdleCallback`, so this degrades to a `setTimeout(0)` there, which still yields to
 * the current task before firing.
 *
 * WHY dependency-injected. `scheduleIdle`/`warmLinkTable`/`warmOrgSelectChunk` all default to the
 * real browser/webpack behaviour, but are accepted as parameters so this file's own tests can
 * verify the scheduling and gating logic without needing a real bundler-driven `import()` (Jest
 * resolves `import()` through babel-plugin-dynamic-import-node -- a real, but synchronous-ish,
 * `require()` -- see App.lazyTableScreens.test.tsx's comment on that) or a real idle callback.
 */

export type IdleScheduler = (callback: () => void) => void;

const defaultIdleScheduler: IdleScheduler = callback => {
	if (typeof requestIdleCallback === "function") {
		requestIdleCallback(() => callback(), { timeout: 2000 });
	} else {
		// Safari (all versions, as of this writing) has no requestIdleCallback. setTimeout(0) is not
		// a real idle callback, but it does defer past the current task, which is enough to avoid
		// contending with whatever synchronous work is still finishing up post-login.
		setTimeout(callback, 0);
	}
};

// Module-level, not component state: this must survive across App re-renders/re-mounts (e.g. the
// ErrorBoundary "Try again" remount App.tsx documents elsewhere) and only ever fire the warm-up
// ONCE per page load, no matter how many times an authenticated App instance asks for it (every
// componentDidUpdate, an org switch, etc.).
let hasWarmed = false;

/**
 * Test-only: clears the one-shot guard above so each test file/case can assert the scheduling
 * logic from a clean slate. Never called from production code.
 */
export function resetPostLoginChunkWarmupForTests(): void {
	hasWarmed = false;
}

/**
 * Schedules the post-login chunk warm-up at most once per page load. Safe to call unconditionally
 * on every render once the caller knows the visitor is authenticated -- the one-shot guard above
 * makes every call after the first a no-op.
 *
 * @param warmOrgSelect Only true for corporate visitors (`corporateUserOrgs.length > 0`): personal
 *   logins never render Layout's org selector, so warming its chunk for them would spend bytes on
 *   a request that route will never make.
 */
export function scheduleWarmPostLoginChunks(
	warmOrgSelect: boolean,
	scheduleIdle: IdleScheduler = defaultIdleScheduler,
	warmLinkTable: () => Promise<unknown> = () => import("../components/linkTable/LinkTable"),
	warmOrgSelectChunk: () => Promise<unknown> = () => import("../components/layout/OrgSelect"),
): void {
	if (hasWarmed) return;
	hasWarmed = true;

	scheduleIdle(() => {
		// Best-effort cache warm only, never a real data dependency: a rejected import() here (e.g.
		// offline, or a CDN blip) must never surface as an app-visible error. The ordinary
		// React.lazy() boundaries in App.tsx/Layout.tsx will simply re-request the chunk themselves,
		// through their own Suspense fallback, when the visitor actually navigates there.
		warmLinkTable().catch(() => undefined);
		if (warmOrgSelect) {
			warmOrgSelectChunk().catch(() => undefined);
		}
	});
}
